/**
 * Lift2Fit — the coaching workspace · Profile & settings.
 *
 * The coach's own page, deliberately NOT the lifter's `#/profile`: a coach's
 * profile is about their BUSINESS. It carries the public profile a lifter sees
 * in discovery (name, places, contact, bio, specialities, languages, years),
 * the discovery opt-in, the review publication switch, the places a coach
 * works from, the coaches on the team, the coaching packs link, and the
 * cabinet: units, theme, timezone, notifications, billing/tier and sign out.
 *
 * The lifter interface is a PAID-plan entitlement (0021). From Coach upward the
 * profile carries an explicit, labelled entry to the lifter shell ("Your lifter
 * profile — included with your plan"); on the free (1-client) plan the entry is
 * absent and the reason plus the upgrade path is stated instead.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  api,
  type CoachPackCadence,
  type CoachProfile,
} from '@/lib/api';
import { cn } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { useTheme, type Theme } from '@/lib/useTheme';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { Button } from '@/components/ui';
import { ArrowRightIcon, CheckIcon, GridIcon, PlusIcon, TrashIcon } from '@/lib/icons';
import { LiveMessage, Panel, ScreenHeader, StatusChip } from '../components';
import { useAsync } from '../hooks';
import { Link, navigate } from '../router';
import { CrmMessage } from './shared';

const CONTACT_KINDS = ['email', 'phone', 'instagram', 'website', 'other'] as const;

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  coach: 'Coach',
  gym: 'Gyms',
  gym_plus: 'Gyms Plus',
  scale: 'Scale',
};

const PLAN_SEATS: Record<string, number | null> = {
  // 0034: coaching is paid — the free plan carries no client seats.
  free: 0,
  coach: 10,
  gym: 20,
  gym_plus: 100,
  scale: null,
};

/** 0030: the internal-coach allowance each plan carries (mirrors the API). */
const PLAN_INTERNAL_COACHES: Record<string, number | null> = {
  free: 0,
  coach: 0,
  gym: 10,
  gym_plus: 20,
  scale: null,
};

const PLAN_SUMMARY: Record<string, string> = {
  free: 'No coaching workspace — coaching is paid. Register as a lifter to train.',
  coach: '1–10 clients, £19.99 / month. For managing your own clients — no internal coaches. Includes your own lifter interface.',
  gym: 'Up to 20 clients and 10 internal coaches, £49 / month. The specialised coach for gyms. Includes your own lifter interface.',
  gym_plus: 'Up to 100 clients and 20 internal coaches, £99 / month. For larger gyms. Includes your own lifter interface.',
  scale: '100+ clients and unlimited internal coaches, negotiated. Includes your own lifter interface — talk to us.',
};

const CADENCE_LABELS: Record<CoachPackCadence, string> = {
  one_off: 'One-off',
  weekly: 'Weekly',
  monthly: 'Monthly',
  pay_as_go: 'Pay as you go (hourly)',
};

const TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Australia/Sydney',
] as const;

function splitList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function listText(values: readonly string[]): string {
  return values.join(', ');
}

interface ProfileDraft {
  discoverable: boolean;
  displayName: string;
  location: string;
  contactKind: string;
  contactValue: string;
  contactPublic: boolean;
  bio: string;
  specialities: string;
  languages: string;
  yearsCoaching: string;
  reviewsPublic: boolean;
}

const EMPTY_PROFILE: ProfileDraft = {
  discoverable: false,
  displayName: '',
  location: '',
  contactKind: 'email',
  contactValue: '',
  contactPublic: false,
  bio: '',
  specialities: '',
  languages: '',
  yearsCoaching: '',
  reviewsPublic: false,
};

function draftFromProfile(profile: CoachProfile): ProfileDraft {
  return {
    discoverable: profile.discoverable,
    displayName: profile.displayName,
    location: profile.location ?? '',
    contactKind: profile.contactKind ?? 'email',
    contactValue: profile.contactValue ?? '',
    contactPublic: profile.contactPublic,
    bio: profile.bio ?? '',
    specialities: listText(profile.specialities),
    languages: listText(profile.languages),
    yearsCoaching: profile.yearsCoaching === null ? '' : String(profile.yearsCoaching),
    reviewsPublic: profile.reviewsPublic,
  };
}

interface LocationDraft {
  label: string;
  address: string;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

export function CrmProfileScreen() {
  const session = useSession();
  const user = session.user;
  const { unit, setUnit } = useUnitPreference();
  const { theme, setTheme } = useTheme();

  const bundle = useAsync(() => api.coachProfileBundle(), []);
  const profile = bundle.data?.profile ?? null;

  const [draft, setDraft] = useState<ProfileDraft>(EMPTY_PROFILE);
  const [loadedSignature, setLoadedSignature] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileTone, setProfileTone] = useState<'info' | 'success' | 'danger'>('info');

  const [locations, setLocations] = useState<LocationDraft[]>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [savingLocations, setSavingLocations] = useState(false);
  const [locationsMessage, setLocationsMessage] = useState<string | null>(null);
  const [locationsTone, setLocationsTone] = useState<'info' | 'success' | 'danger'>('info');

  // 0030/0033: ONE team surface. The coaches who work here are real accounts,
  // added by invitation — there is no separate name-only roster any more.
  const internal = useAsync(() => api.internalCoaches(), []);

  const reviews = useAsync(() => api.coachReceivedReviews(), []);

  const [timezone, setTimezone] = useState(user?.timezone ?? 'UTC');
  const [accountMessage, setAccountMessage] = useState<string | null>(null);
  const [accountTone, setAccountTone] = useState<'info' | 'success' | 'danger'>('info');
  const [savingAccount, setSavingAccount] = useState(false);

  // Adopt the saved profile once it arrives, keyed by updatedAt so an
  // in-progress edit is never clobbered by an unrelated re-render.
  const signature = profile ? `${profile.coachId}:${profile.updatedAt}` : null;
  useEffect(() => {
    if (!profile || signature === null || signature === loadedSignature) return;
    setDraft(draftFromProfile(profile));
    setLoadedSignature(signature);
  }, [profile, signature, loadedSignature]);

  const loadLocations = useAsync(() => api.coachLocations(), []);
  useEffect(() => {
    if (locationsLoaded || loadLocations.loading) return;
    if (loadLocations.data === null && loadLocations.error) return;
    setLocations(
      (loadLocations.data ?? []).map((location) => ({
        label: location.label ?? '',
        address: location.address,
      })),
    );
    setLocationsLoaded(true);
  }, [loadLocations.data, loadLocations.loading, loadLocations.error, locationsLoaded]);

  if (!user) return null;

  const plan = user.coachPlan;
  const seatLimit = PLAN_SEATS[plan] ?? null;
  const lifterIncluded = user.lifterInterface;

  // 0033: only a plan that INCLUDES internal coaches shows the team surface.
  // `limit === null` is unlimited (Scale); 0 is free and Coach, which have no
  // team feature — the panel is not rendered at all for those, so there is
  // nothing to click and no invitation to send.
  const canHaveTeam =
    internal.data !== null && (internal.data.limit === null || internal.data.limit > 0);
  const joinedCoaches = (internal.data?.data ?? []).filter((coach) => coach.status === 'active').length;
  const waitingInvites = (internal.data?.invites ?? []).filter(
    (invite) => invite.status === 'pending' && !invite.expired,
  ).length;

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileTone('info');
    setProfileMessage('Saving…');
    const result = await api.saveCoachProfile({
      discoverable: draft.discoverable,
      displayName: draft.displayName.trim() === '' ? null : draft.displayName.trim(),
      location: draft.location.trim() === '' ? null : draft.location.trim(),
      contactKind: draft.contactKind === '' ? null : draft.contactKind,
      contactValue: draft.contactValue.trim() === '' ? null : draft.contactValue.trim(),
      contactPublic: draft.contactPublic,
      bio: draft.bio.trim() === '' ? null : draft.bio.trim(),
      specialities: splitList(draft.specialities),
      languages: splitList(draft.languages),
      yearsCoaching: draft.yearsCoaching.trim() === '' ? null : Number(draft.yearsCoaching),
      reviewsPublic: draft.reviewsPublic,
    });
    setSavingProfile(false);
    if (result.problem || !result.data) {
      setProfileTone('danger');
      setProfileMessage(result.problem?.detail ?? 'The profile did not save.');
      return;
    }
    setLoadedSignature(`${result.data.coachId}:${result.data.updatedAt}`);
    setProfileTone('success');
    setProfileMessage(
      result.data.discoverable
        ? 'Profile saved — you are listed in discovery with the fields you published.'
        : 'Profile saved — you are hidden from discovery. Your packs stay yours.',
    );
    bundle.reload();
  };

  const saveLocations = async () => {
    setSavingLocations(true);
    setLocationsTone('info');
    setLocationsMessage('Saving…');
    const cleaned = locations
      .map((location) => ({ label: location.label.trim(), address: location.address.trim() }))
      .filter((location) => location.address !== '');
    const result = await api.saveCoachLocations(
      cleaned.map((location, index) => ({
        label: location.label === '' ? null : location.label,
        address: location.address,
        sortOrder: index,
      })),
    );
    setSavingLocations(false);
    if (result.problem) {
      setLocationsTone('danger');
      setLocationsMessage(result.problem.detail ?? 'The places did not save.');
      return;
    }
    setLocations((result.data ?? []).map((location) => ({ label: location.label ?? '', address: location.address })));
    setLocationsTone('success');
    setLocationsMessage(
      cleaned.length === 0
        ? 'No places published — your discovery profile shows no address.'
        : `Saved ${cleaned.length} ${cleaned.length === 1 ? 'place' : 'places'} — they appear on your discovery profile.`,
    );
  };

  const saveAccount = async (patch: { timezone?: string; unitSystem?: 'metric' | 'imperial' }) => {
    setSavingAccount(true);
    setAccountTone('info');
    setAccountMessage('Saving…');
    const result = await api.updateMe(patch);
    setSavingAccount(false);
    if (result.problem || !result.data) {
      setAccountTone('danger');
      setAccountMessage(result.problem?.detail ?? 'That setting did not save.');
      return;
    }
    session.updateUser({
      timezone: result.data.timezone,
      unitSystem: result.data.unitSystem,
    });
    if (patch.timezone) setTimezone(result.data.timezone);
    setAccountTone('success');
    setAccountMessage('Saved to your account.');
  };

  const signOut = () => {
    session.logout();
    navigate('/login', { replace: true });
  };

  const reviewSummary = reviews.data;

  return (
    <div className="flex flex-col gap-6" data-testid="crm-profile">
      <ScreenHeader
        eyebrow="Coaching workspace"
        title="Profile & settings"
        lead="Your business page: what lifters see in discovery, the coaches who work with you, and the settings for this account."
        badge={<StatusChip tone="info">{PLAN_LABELS[plan] ?? 'Free'} plan</StatusChip>}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          {/* 1 — the public profile a lifter sees in discovery. */}
          <Panel
            testId="crm-coach-profile"
            title="Public profile"
            subtitle="Off by default. Only the fields you publish appear in discovery — never your clients, revenue or diary."
            actions={
              profile?.discoverable ? (
                <StatusChip tone="success">Listed</StatusChip>
              ) : (
                <StatusChip tone="neutral">Hidden</StatusChip>
              )
            }
          >
            {bundle.loading && !bundle.data ? (
              <p className="text-[13px] text-ink-muted">Loading your profile…</p>
            ) : bundle.error ? (
              <LiveMessage message={bundle.error} tone="danger" />
            ) : (
              <form className="flex flex-col gap-4" onSubmit={saveProfile} data-testid="coach-profile-form">
                <label className="flex items-start gap-3 border border-hairline bg-page-subtle px-3 py-3">
                  <input
                    type="checkbox"
                    data-testid="coach-discoverable"
                    checked={draft.discoverable}
                    onChange={(event) => setDraft({ ...draft, discoverable: event.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-[var(--l2f-accent)]"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-bold text-ink">Get discovered on Lift2Fit</span>
                    <span className="text-[12px] text-ink-muted">
                      When on, lifters can find you by name, place or pack and read the profile below.
                    </span>
                  </span>
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Business display name">
                    <input
                      type="text"
                      data-testid="coach-profile-name"
                      value={draft.displayName}
                      onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
                      placeholder="Aisha Barbell Coaching"
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    />
                  </Field>
                  <Field label="Primary location">
                    <input
                      type="text"
                      data-testid="coach-profile-location"
                      value={draft.location}
                      onChange={(event) => setDraft({ ...draft, location: event.target.value })}
                      placeholder="Leeds"
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    />
                  </Field>
                  <Field label="Contact method">
                    <select
                      data-testid="coach-profile-contact-kind"
                      value={draft.contactKind}
                      onChange={(event) => setDraft({ ...draft, contactKind: event.target.value })}
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    >
                      {CONTACT_KINDS.map((kind) => (
                        <option key={kind} value={kind}>
                          {kind}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Contact detail">
                    <input
                      type="text"
                      data-testid="coach-profile-contact-value"
                      value={draft.contactValue}
                      onChange={(event) => setDraft({ ...draft, contactValue: event.target.value })}
                      placeholder="aisha@example.com"
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    />
                  </Field>
                  <Field label="Years coaching">
                    <input
                      type="number"
                      min={0}
                      max={80}
                      data-testid="coach-profile-years"
                      value={draft.yearsCoaching}
                      onChange={(event) => setDraft({ ...draft, yearsCoaching: event.target.value })}
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    />
                  </Field>
                  <Field label="Specialities (comma separated)">
                    <input
                      type="text"
                      data-testid="coach-profile-specialities"
                      value={draft.specialities}
                      onChange={(event) => setDraft({ ...draft, specialities: event.target.value })}
                      placeholder="Powerlifting, Strength"
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    />
                  </Field>
                  <Field label="Languages (comma separated)">
                    <input
                      type="text"
                      data-testid="coach-profile-languages"
                      value={draft.languages}
                      onChange={(event) => setDraft({ ...draft, languages: event.target.value })}
                      placeholder="English, Arabic"
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    />
                  </Field>
                </div>

                <Field label="Short bio">
                  <textarea
                    data-testid="coach-profile-bio"
                    value={draft.bio}
                    onChange={(event) => setDraft({ ...draft, bio: event.target.value })}
                    rows={3}
                    placeholder="What you coach, who you coach, and how you work."
                    className="border border-hairline bg-page px-3 py-2 text-[15px] text-ink"
                  />
                </Field>

                <label className="flex items-start gap-3 border border-hairline px-3 py-3">
                  <input
                    type="checkbox"
                    data-testid="coach-contact-public"
                    checked={draft.contactPublic}
                    onChange={(event) => setDraft({ ...draft, contactPublic: event.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-[var(--l2f-accent)]"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-bold text-ink">Publish my contact method</span>
                    <span className="text-[12px] text-ink-muted">
                      Without this, the contact detail above is stored but never listed.
                    </span>
                  </span>
                </label>

                {/* Reviews publication is the coach's own choice, default OFF. */}
                <label className="flex items-start gap-3 border border-hairline px-3 py-3">
                  <input
                    type="checkbox"
                    data-testid="coach-reviews-public"
                    checked={draft.reviewsPublic}
                    onChange={(event) => setDraft({ ...draft, reviewsPublic: event.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-[var(--l2f-accent)]"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-bold text-ink">Publish my rating and reviews</span>
                    <span className="text-[12px] text-ink-muted">
                      Off by default. When off, your score stays yours and nothing about ratings appears on
                      your discovery profile. Turn it on when you are ready for lifters to see it.
                    </span>
                  </span>
                </label>

                <div className="flex flex-wrap items-center gap-2">
                  <Button type="submit" disabled={savingProfile} data-testid="coach-profile-save">
                    {savingProfile ? 'Saving…' : 'Save profile'}
                  </Button>
                </div>
                <LiveMessage message={profileMessage} tone={profileTone} />
              </form>
            )}
          </Panel>

          {/* 2 — the places a coach works from. Multiple, keyless map links. */}
          <Panel
            testId="crm-coach-locations"
            title="Places"
            subtitle="Every place you coach from. Discovery lists them with a keyless 'view on map' link — no map account needed."
            actions={
              <Button
                variant="secondary"
                size="sm"
                data-testid="coach-location-add"
                onClick={() => setLocations((current) => [...current, { label: '', address: '' }])}
              >
                Add a place
              </Button>
            }
          >
            {locations.length === 0 ? (
              <p className="text-[13px] text-ink-muted" data-testid="coach-locations-empty">
                No places yet. Add one and it appears on your discovery profile as a plain address.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {locations.map((location, index) => (
                  <li key={index} className="grid gap-3 border border-hairline p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]">
                    <Field label="Label (optional)">
                      <input
                        type="text"
                        value={location.label}
                        onChange={(event) =>
                          setLocations((current) =>
                            current.map((entry, i) =>
                              i === index ? { ...entry, label: event.target.value } : entry,
                            ),
                          )
                        }
                        placeholder="City centre studio"
                        className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                      />
                    </Field>
                    <Field label="Address">
                      <input
                        type="text"
                        data-testid={`coach-location-address-${index}`}
                        value={location.address}
                        onChange={(event) =>
                          setLocations((current) =>
                            current.map((entry, i) =>
                              i === index ? { ...entry, address: event.target.value } : entry,
                            ),
                          )
                        }
                        placeholder="12 Vicar Lane, Leeds LS1"
                        className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                      />
                    </Field>
                    <div className="flex items-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`coach-location-remove-${index}`}
                        onClick={() => setLocations((current) => current.filter((_, i) => i !== index))}
                      >
                        <TrashIcon /> Remove
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                disabled={savingLocations}
                data-testid="coach-locations-save"
                onClick={() => void saveLocations()}
              >
                {savingLocations ? 'Saving…' : 'Save places'}
              </Button>
            </div>
            <LiveMessage message={locationsMessage} tone={locationsTone} />
          </Panel>

          {/* 3 — one team surface. The coaches who work here are real accounts,
              added by invitation; only a GYM has them (0030/0033). An internal
              coach has no team of their own, and a simple coach does not get the
              option at all. */}
          {canHaveTeam ? (
            <Panel
              testId="crm-coach-team"
              title="Coaches on the team"
              subtitle="Your coaches live on the Team page: invitations, the nickname you recognise a coach by until they join, and what each of them is carrying. Only a gym plan has them, and a coach you invite sees only the clients you assign."
            >
              <div className="flex flex-col gap-3" data-testid="crm-internal-coaches">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink">
                    Your coaches
                  </span>
                  {internal.data ? (
                    <span className="text-[12px] text-ink-muted" data-testid="internal-coach-cap">
                      {internal.data.used}
                      {internal.data.limit === null ? '' : ` of ${internal.data.limit}`} internal coach
                      {internal.data.limit === 1 ? '' : 'es'} · {PLAN_LABELS[plan] ?? plan} plan
                    </span>
                  ) : null}
                </div>
                <p className="text-[12px] text-ink-muted">
                  {internal.data
                    ? `${joinedCoaches} joined${waitingInvites > 0 ? ` · ${waitingInvites} invitation${waitingInvites === 1 ? '' : 's'} waiting` : ''}.`
                    : 'Loading your team…'}{' '}
                  Open the Team page to invite, withdraw and see each coach’s summary.
                </p>
                <Link
                  to="/crm/team"
                  data-testid="crm-team-link"
                  className="flex items-center justify-between gap-3 border border-hairline px-4 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-ink hover:border-accent hover:text-accent"
                >
                  <span className="flex items-center gap-2">
                    <GridIcon className="text-[15px] text-accent" />
                    Manage your coaches
                  </span>
                  <ArrowRightIcon />
                </Link>
              </div>
            </Panel>
          ) : null}
        </div>

        <div className="flex flex-col gap-4">
          {/* 4 — the lifter interface is included from the paid plan upward. */}
          <Panel
            testId="crm-lifter-interface"
            title="Your lifter profile"
            subtitle="One account, two interfaces. Your clients' training lives in this workspace; the lifter interface is your own training."
          >
            {lifterIncluded ? (
              <>
                <p className="border-l-2 border-l-accent bg-page-subtle px-3 py-2 text-[13px] text-ink">
                  <span className="font-bold uppercase tracking-[0.06em]">Included with your plan:</span>{' '}
                  a coaching workspace, plus a free lifter account for your own training.
                </p>
                <Link
                  to="/dashboard"
                  data-testid="coach-lifter-entry"
                  className="flex items-center justify-between gap-3 border border-hairline px-4 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-ink hover:border-accent hover:text-accent"
                >
                  <span className="flex items-center gap-2">
                    <GridIcon className="text-[15px] text-accent" />
                    Your lifter profile — included with your plan
                  </span>
                  <ArrowRightIcon />
                </Link>
              </>
            ) : (
              <div className="flex flex-col gap-3" data-testid="coach-lifter-upgrade">
                <p className="text-[13px] text-ink">
                  Coaching is a paid product, so this account has no workspace yet: no clients, no packs, no
                  discovery, and no lifter interface. The Coach plan (£19.99 / month, 10 clients) includes
                  your own training as well — a coaching workspace, plus a lifter account for yourself.
                  Just want to train? A free lifter account does that.
                </p>
                <a
                  href="/for-coaches"
                  className="inline-flex h-11 w-fit items-center justify-center gap-2 bg-accent px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
                >
                  See the Coach plan
                </a>
              </div>
            )}
          </Panel>

          {/* 5 — packs live in their own screen; the profile links to it. */}
          <Panel
            testId="crm-profile-packs"
            title="Coaching packs"
            subtitle="What lifters sign up for: price, cadence, session length, booking windows, sharing and coaches."
            actions={
              <Link
                to="/crm/packs"
                data-testid="crm-profile-packs-link"
                className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
              >
                Manage packs
                <ArrowRightIcon />
              </Link>
            }
          >
            <p className="text-[13px] text-ink-muted">
              {(bundle.data?.packs ?? []).length === 0
                ? 'No packs yet. Create one and a lifter can discover and sign up for it.'
                : `${(bundle.data?.packs ?? []).length} ${(bundle.data?.packs ?? []).length === 1 ? 'pack' : 'packs'} on your profile.`}
            </p>
          </Panel>

          {/* 6 — the coach always sees their own score, published or not. */}
          <Panel
            testId="crm-coach-reviews"
            title="Your reviews"
            subtitle="Always visible to you. Whether lifters see them on your discovery profile is your choice above."
            actions={
              reviewSummary && reviewSummary.count > 0 ? (
                <StatusChip tone="neutral">
                  {reviewSummary.average?.toFixed(2) ?? '—'} / 5 · {reviewSummary.count}{' '}
                  {reviewSummary.count === 1 ? 'review' : 'reviews'}
                </StatusChip>
              ) : null
            }
          >
            {reviews.loading && !reviews.data ? (
              <p className="text-[13px] text-ink-muted">Loading your score…</p>
            ) : reviews.error ? (
              <LiveMessage message={reviews.error} tone="danger" />
            ) : !reviewSummary || reviewSummary.count === 0 ? (
              <p className="text-[13px] text-ink-muted" data-testid="coach-reviews-empty">
                No reviews yet. Your score stays private until you publish it.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-2" data-testid="coach-review-distribution">
                  {(['5', '4', '3', '2', '1'] as const).map((star) => (
                    <span
                      key={star}
                      className="inline-flex items-center gap-2 border border-hairline px-2 py-1 text-[12px] text-ink"
                    >
                      <span className="font-bold">{star}</span>
                      <span className="text-ink-muted">stars · {reviewSummary.distribution[star]}</span>
                    </span>
                  ))}
                </div>
                <ul className="flex flex-col gap-2">
                  {reviewSummary.recent.map((review) => (
                    <li key={review.reviewId} className="border border-hairline px-3 py-2">
                      <p className="text-[13px] font-bold text-ink">
                        {review.rating} / 5 · {review.lifterName}
                      </p>
                      {review.comment ? (
                        <p className="text-[13px] text-ink-muted">{review.comment}</p>
                      ) : (
                        <p className="text-[12px] text-ink-muted">No comment left.</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Panel>

          {/* 7 — billing/tier. */}
          <Panel
            testId="crm-coach-billing"
            title="Plan & billing"
            subtitle="Your coaching tier and the seats it includes."
          >
            <dl className="flex flex-col gap-2 text-[13px]">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Plan</dt>
                <dd className="font-bold text-ink" data-testid="coach-plan-label">
                  {PLAN_LABELS[plan] ?? 'Free'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-ink-muted">Clients included</dt>
                <dd className="font-bold text-ink" data-testid="coach-seat-limit">
                  {seatLimit === null ? 'Unlimited (negotiated)' : seatLimit}
                </dd>
              </div>
            </dl>
            <p className="text-[13px] text-ink-muted">{PLAN_SUMMARY[plan] ?? PLAN_SUMMARY.free}</p>
            {plan === 'free' ? (
              <a
                href="/for-coaches"
                className="inline-flex h-11 w-fit items-center justify-center gap-2 border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
              >
                Compare plans
              </a>
            ) : null}
          </Panel>

          {/* 8 — the cabinet: units, theme, timezone. */}
          <Panel
            testId="crm-coach-preferences"
            title="Preferences"
            subtitle="Units change display only; canonical storage stays kilograms."
          >
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Units</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="coach-unit-metric"
                  aria-pressed={unit === 'metric'}
                  onClick={() => {
                    setUnit('metric');
                    void saveAccount({ unitSystem: 'metric' });
                  }}
                  className={cn(
                    'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                    unit === 'metric'
                      ? 'border-ink bg-ink text-ink-inverse'
                      : 'border-hairline bg-surface text-ink hover:border-accent',
                  )}
                >
                  Kilograms
                </button>
                <button
                  type="button"
                  data-testid="coach-unit-imperial"
                  aria-pressed={unit === 'imperial'}
                  onClick={() => {
                    setUnit('imperial');
                    void saveAccount({ unitSystem: 'imperial' });
                  }}
                  className={cn(
                    'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                    unit === 'imperial'
                      ? 'border-ink bg-ink text-ink-inverse'
                      : 'border-hairline bg-surface text-ink hover:border-accent',
                  )}
                >
                  Pounds
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Theme</span>
              <div className="flex flex-wrap gap-2">
                {(['light', 'dark'] as const).map((option: Theme) => (
                  <button
                    key={option}
                    type="button"
                    data-testid={`coach-theme-${option}`}
                    aria-pressed={theme === option}
                    onClick={() => setTheme(option)}
                    className={cn(
                      'h-11 border px-4 text-[12px] font-bold uppercase tracking-[0.08em]',
                      theme === option
                        ? 'border-ink bg-ink text-ink-inverse'
                        : 'border-hairline bg-surface text-ink hover:border-accent',
                    )}
                  >
                    {option === 'light' ? 'Light' : 'Dark'}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Timezone">
              <select
                data-testid="coach-timezone"
                value={timezone}
                onChange={(event) => {
                  setTimezone(event.target.value);
                  void saveAccount({ timezone: event.target.value });
                }}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              >
                {[...new Set([timezone, ...TIMEZONES])].map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </Field>
            <p className="text-[12px] text-ink-muted">
              Your booking windows are evaluated in this timezone.
            </p>
            <LiveMessage message={accountMessage} tone={accountTone} />
          </Panel>

          {/* 9 — notifications: honest about what exists today. */}
          <Panel
            testId="crm-coach-notifications"
            title="Notifications"
            subtitle="What the workspace tells you about, and where."
          >
            <ul className="flex flex-col gap-2 text-[13px] text-ink-muted">
              <li className="flex items-start gap-2">
                <CheckIcon className="mt-0.5 text-success" />
                New bookings and cancellations appear on the Calendar as they happen.
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="mt-0.5 text-success" />
                Blocks ending soon appear on the Clients roster and in To-do.
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="mt-0.5 text-success" />
                Due payments appear in the Payments ledger.
              </li>
            </ul>
            <p className="text-[12px] text-ink-muted">
              There is no email or push notification yet, so nothing is promised that is not delivered. Email
              reminders are recorded as deferred in the platform plan.
            </p>
          </Panel>

          <Panel title="Session" subtitle={`Signed in as ${user.email}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" data-testid="coach-signout" onClick={signOut}>
                Sign out
              </Button>
              <span className="text-[12px] text-ink-muted">
                {session.isDemo ? 'Demo session — nothing is stored on a server.' : 'Tokens are cleared from this device.'}
              </span>
            </div>
          </Panel>

          {/* A coach in the coach shell must never be shown the lifter profile. */}
          <p className="text-[12px] text-ink-muted">
            This is your business profile. The lifter profile and settings live at <code>#/profile</code> and
            are only reachable through the lifter shell. {(session.user?.roles ?? []).includes('lifter')
              ? 'Your account still holds the lifter role; the plan decides whether the lifter interface is included.'
              : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
